import React, { createContext, useState, useEffect, useRef } from "react";
import { api, API_BASE_URL } from "../config/api";
import { useMyContext } from "./MyContext";

export const CurrencyContext = createContext();

export const CurrencyProvider = ({ children }) => {
  const { token } = useMyContext();
  const [currencies, setCurrencies] = useState([]);
  const lastFetchTimeRef = useRef(null);
  const fetchInProgressRef = useRef(false);

  // Fetch currencies from the API with optimization
  const fetchCurrencies = async (force = false) => {
    if (fetchInProgressRef.current) return;

    const currentTime = Date.now();
    if (!force && lastFetchTimeRef.current &&
      currentTime - lastFetchTimeRef.current < 5 * 60 * 1000) {
      return;
    }

    try {
      fetchInProgressRef.current = true;
      const response = await api.get(`${API_BASE_URL}/currency`);

      if (response.status === 200) {
        // console.log("Fetched Currencies:", response.data);
        setCurrencies(response.data);
        lastFetchTimeRef.current = currentTime;
      }
    } catch (error) {
      console.error("Error fetching currencies:", error);
    } finally {
      fetchInProgressRef.current = false;
    }
  };

  const addCurrency = async (newCurrency) => {
    try {
      const response = await api.post(`${API_BASE_URL}/currency`, newCurrency);

      if (response.status === 201) {
        await fetchCurrencies(true);
      }

      return response;
    } catch (error) {
      console.error("Error adding currency:", error);
      throw error;
    }
  };

  // Currency conversion function
  const convertCurrency = async (amount, fromCurrency, toCurrency) => {
    try {
      const response = await api.post(`${API_BASE_URL}/currency/convert`, {
        amount: parseFloat(amount),
        fromCurrency,
        toCurrency
      });

      if (response.status === 200) {
        return response.data;
      } else {
        throw new Error("Conversion failed");
      }
    } catch (error) {
      console.error("Error converting currency:", error);
      throw error;
    }
  };

  // Get available currencies for conversion
  const getAvailableCurrencies = () => {
    return currencies.map(currency => ({
      name: currency.name,
      symbol: currency.symbol,
      exchangeRates: currency.exchangeRates
    }));
  };

  // Get exchange rate between two currencies
  const getExchangeRate = (fromCurrency, toCurrency) => {
    if (fromCurrency === toCurrency) return 1;

    const fromCurrencyData = currencies.find(c => c.name === fromCurrency);
    if (!fromCurrencyData) return null;

    return fromCurrencyData.exchangeRates?.[toCurrency] || null;
  };

  // Check if conversion is possible between two currencies
  const canConvert = (fromCurrency, toCurrency) => {
    if (fromCurrency === toCurrency) return true;

    const rate = getExchangeRate(fromCurrency, toCurrency);
    return rate !== null && rate !== undefined;
  };

  useEffect(() => {
    if (token) {
      fetchCurrencies();
    }
  }, [token]);

  return (
    <CurrencyContext.Provider value={{
      currencies,
      addCurrency,
      fetchCurrencies: () => fetchCurrencies(true),
      convertCurrency,
      getAvailableCurrencies,
      getExchangeRate,
      canConvert
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};